import * as CookieConsent from 'vanilla-cookieconsent';

export default class ConsentManager {
  constructor() {
    // Check if we're in dev mode
    this.isDev = window.seomaticScripts?.isDev || false;
    
    if (this.isDev) {
      console.log('🔧 DEV MODE: Cookie consent in development mode');
      // In dev mode, auto-grant consent and load scripts immediately
      this.autoLoadDevScripts();
    }
    
    this.init();
    // Expose CookieConsent globally for the cookie policy page button
    window.CookieConsent = CookieConsent;

    if (this.isDev) {
      console.log('CookieConsent initialized');
      console.log('SEOmatic scripts config:', window.seomaticScripts);
    }
  }

  autoLoadDevScripts() {
    // In dev mode, auto-load configured scripts
    if (window.seomaticScripts?.analytics?.config?.measurementId) {
      setTimeout(() => {
        this.loadGoogleAnalytics(window.seomaticScripts.analytics.config.measurementId);
        window.seomaticScripts.analytics.loaded = true;
      }, 100);
    }
    
    if (window.seomaticScripts?.marketing?.config?.containerId) {
      setTimeout(() => {
        this.loadGoogleTagManager(window.seomaticScripts.marketing.config.containerId);
        window.seomaticScripts.marketing.loaded = true;
      }, 100);
    }
  }

  init() {
    // Enable dark mode
    document.documentElement.classList.add('cc--elegant-black');
    
    CookieConsent.run({
      categories: {
        necessary: {
          enabled: true,
          readOnly: true
        },
        analytics: {
          enabled: false,
          readOnly: false,
          autoClear: {
            cookies: [
              {
                name: /^(_ga|_gid|_gat)/
              }
            ]
          }
        },
        marketing: {
          enabled: false,
          readOnly: false
        }
      },

      language: {
        default: 'en',
        translations: {
          en: {
            consentModal: {
              title: 'We use cookies',
              description: 'We use cookies to enhance your browsing experience, serve personalised content, and analyse our traffic. By clicking "Accept all", you consent to our use of cookies.',
              acceptAllBtn: 'Accept all',
              acceptNecessaryBtn: 'Reject all',
              showPreferencesBtn: 'Manage preferences',
              footer: '<a href="/privacy">Privacy &amp; Cookie Policy</a>'
            },
            preferencesModal: {
              title: 'Cookie Preferences',
              acceptAllBtn: 'Accept all',
              acceptNecessaryBtn: 'Reject all',
              savePreferencesBtn: 'Save preferences',
              closeIconLabel: 'Close',
              serviceCounterLabel: 'Service|Services',
              sections: [
                {
                  title: 'Cookie Usage',
                  description: 'We use cookies to ensure the basic functionalities of the website and to enhance your online experience. You can choose for each category to opt-in/out whenever you want.'
                },
                {
                  title: 'Strictly Necessary Cookies <span class="pm__badge">Always Enabled</span>',
                  description: 'These cookies are essential for the proper functioning of the website. Without these cookies, the website would not work properly.',
                  linkedCategory: 'necessary'
                },
                {
                  title: 'Analytics Cookies',
                  description: 'These cookies collect information about how you use the website, which pages you visited and which links you clicked on. All of the data is anonymized and cannot be used to identify you.',
                  linkedCategory: 'analytics',
                  cookieTable: {
                    headers: {
                      name: 'Name',
                      domain: 'Domain',
                      description: 'Description',
                      expiration: 'Expiration'
                    },
                    body: [
                      {
                        name: '_ga',
                        domain: location.hostname,
                        description: 'Google Analytics cookie used to distinguish users.',
                        expiration: '2 years'
                      },
                      {
                        name: '_gid',
                        domain: location.hostname,
                        description: 'Google Analytics cookie used to distinguish users.',
                        expiration: '24 hours'
                      }
                    ]
                  }
                },
                {
                  title: 'Marketing Cookies',
                  description: 'These cookies are used to track visitors across websites. The intention is to display ads that are relevant and engaging for the individual user.',
                  linkedCategory: 'marketing'
                },
                {
                  title: 'More information',
                  description: 'For any queries in relation to our policy on cookies and your choices, please <a class="cc__link" href="/contact">contact us</a>.'
                }
              ]
            }
          }
        }
      },

      guiOptions: {
        consentModal: {
          layout: 'box',
          position: 'bottom right',
          equalWeightButtons: false,
          flipButtons: false
        },
        preferencesModal: {
          layout: 'box',
          position: 'right',
          equalWeightButtons: false,
          flipButtons: false
        }
      },

      onFirstConsent: ({ cookie }) => {
        if (this.isDev) console.log('onFirstConsent fired', cookie);
      },

      onConsent: ({ cookie }) => {
        if (this.isDev) console.log('onConsent fired', cookie);
        this.handleConsentChange(cookie);
      },

      onChange: ({ changedCategories, changedServices }) => {
        if (this.isDev) console.log('onChange fired', changedCategories, changedServices);
        this.handleConsentChange(CookieConsent.getCookie());
      }
    });
  }

  handleConsentChange(cookie) {
    // Enable/disable tracking based on consent
    if (cookie.categories.includes('analytics')) {
      this.enableAnalytics();
    } else {
      this.disableAnalytics();
    }

    if (cookie.categories.includes('marketing')) {
      this.enableMarketing();
    } else {
      this.disableMarketing();
    }
  }

  enableAnalytics() {
    if (this.isDev) console.log('Analytics enabled');
    
    // Update Google Consent Mode
    if (typeof gtag === 'function') {
      gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
    
    // Load Google Analytics script if configured and not already loaded
    if (window.seomaticScripts?.analytics?.config?.measurementId && !window.seomaticScripts.analytics.loaded) {
      this.loadGoogleAnalytics(window.seomaticScripts.analytics.config.measurementId);
      window.seomaticScripts.analytics.loaded = true;
    }
  }

  disableAnalytics() {
    if (this.isDev) console.log('Analytics disabled');
    
    // Update Google Consent Mode to denied
    if (typeof gtag === 'function') {
      gtag('consent', 'update', {
        'analytics_storage': 'denied'
      });
    }
  }

  enableMarketing() {
    if (this.isDev) console.log('Marketing enabled');
    
    // Update Google Consent Mode
    if (typeof gtag === 'function') {
      gtag('consent', 'update', {
        'ad_storage': 'granted',
        'ad_user_data': 'granted',
        'ad_personalization': 'granted'
      });
    }
    
    // Load GTM script if configured and not already loaded
    if (window.seomaticScripts?.marketing?.config?.containerId && !window.seomaticScripts.marketing.loaded) {
      this.loadGoogleTagManager(window.seomaticScripts.marketing.config.containerId);
      window.seomaticScripts.marketing.loaded = true;
    }
  }

  disableMarketing() {
    if (this.isDev) console.log('Marketing disabled');
    
    // Update Google Consent Mode to denied
    if (typeof gtag === 'function') {
      gtag('consent', 'update', {
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied'
      });
    }
  }

  loadGoogleAnalytics(measurementId) {
    if (this.isDev) console.log('Loading Google Analytics:', measurementId);
    
    // Load gtag.js script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);
    
    // Initialize gtag
    script.onload = () => {
      if (typeof gtag === 'function') {
        gtag('js', new Date());
        gtag('config', measurementId, {
          'send_page_view': true,
          'anonymize_ip': true
        });
      }
    };
  }

  loadGoogleTagManager(containerId) {
    if (this.isDev) console.log('Loading Google Tag Manager:', containerId);
    
    // GTM script
    (function(w,d,s,l,i){
      w[l]=w[l]||[];
      w[l].push({'gtm.start': new Date().getTime(), event:'gtm.js'});
      var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),
          dl=l!='dataLayer'?'&l='+l:'';
      j.async=true;
      j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
      f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer',containerId);
  }
}
